// components/elements/imageViewer/ImageGallery.tsx
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon, DocumentIcon, PlayCircleIcon } from '@heroicons/react/24/solid';
import { Transition } from '@headlessui/react';

interface Image {
  id: string | number;
  docUrl: string;
  docType?: string;
  [key: string]: any;
}

interface ImageViewerProps {
  images: Image[];
  isOpen: boolean;
  currentIndex: number;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
}

const isImage = (url: string) => {
  // Handle both URLs with and without query parameters, and with/without dots before extension
  return /(?:^|\/)([^/?#]+\.(jpe?g|png|gif|webp))(?=[?#]|$)/i.test(url) ||
         /(?:^|\/)([^/?#]*\.(jpe?g|png|gif|webp))(?:[?#].*)?$/i.test(url);
};
const isVideo = (url: string) => {
  // Handle URLs with query parameters and file extensions
  return /(?:^|\/)([^/?#]+\.(mp4|webm|ogg))(?=[?#]|$)/i.test(url) ||
         /(?:^|\/)([^/?#]*\.(mp4|webm|ogg))(?:[?#].*)?$/i.test(url);
};

const isPDF = (url: string) => {
  // Handle URLs with query parameters and .pdf extension
  return /(?:^|\/)([^/?#]+\.pdf)(?=[?#]|$)/i.test(url) ||
         /(?:^|\/)([^/?#]*\.pdf)(?:[?#].*)?$/i.test(url);
};

const MediaDisplay: React.FC<{ url: string; type?: string }> = ({ url, type }) => {
  if (isImage(url)) {
    return (
      <img
        src={url}
        alt={type || 'Image'}
        className="max-h-[80vh] max-w-full object-cover w-full" 
      />
    );
  }

  if (isVideo(url)) {
    return (
      <video
        src={url}
        controls
        className="max-h-[80vh] max-w-full rounded-lg"
      />
    );
  }

  if (isPDF(url)) {
    return (
      <iframe
        src={url}
        className="max-h-[80vh] max-w-full rounded-lg"
        title="PDF Document"
      />
    );
  }

  return (
    <div className="text-white text-center">
      <p>Unsupported file type</p>
      <a href={url} target="_blank" rel="noopener noreferrer" className="underline">
        Open file
      </a>
    </div>
  );
};

const Thumbnail: React.FC<{ url: string; type?: string; active: boolean }> = ({ url, type, active }) => {
  if (isImage(url)) {
    return (
      <img
        src={url}
        alt={type || 'Thumbnail'}
        className="w-full h-full object-cover"
      />
    );
  }

  if (isVideo(url)) {
    return (
      <div className="flex items-center justify-center w-full h-full bg-black/50 text-white">
        <PlayCircleIcon className="w-6 h-6" />
      </div>
    );
  }

  if (isPDF(url)) {
    return (
      <div className="flex items-center justify-center w-full h-full bg-gray-200 text-gray-600">
        <DocumentIcon className="w-6 h-6" />
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center w-full h-full bg-gray-300 text-gray-600">
      ?
    </div>
  );
};

const ImageViewer: React.FC<ImageViewerProps> = ({ images, isOpen, currentIndex, onClose, onNext, onPrev }) => {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isOpen) return;
    switch (e.key) {
      case 'Escape':
        onClose();
        break;
      case 'ArrowLeft':
        onPrev();
        break;
      case 'ArrowRight':
        onNext();
        break;
      default:
        break;
    }
  }, [isOpen, onClose, onNext, onPrev]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen || !images.length) return null;

  return (
    <Transition
      show={isOpen}
      enter="transition-opacity duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity duration-300"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div
        className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white p-2 hover:bg-white/10 rounded-full transition-colors"
          aria-label="Close"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>

        <button
          onClick={(e) => { e.stopPropagation(); onPrev(); }}
          className="absolute left-4 p-2 text-white hover:bg-white/10 rounded-full transition-colors"
          disabled={images.length <= 1}
          aria-label="Previous media"
        >
          <ChevronLeftIcon className="h-8 w-8" />
        </button>

        <div className="relative max-w-full max-h-[80vh]">
          <MediaDisplay
            url={images[currentIndex]?.docUrl}
            type={images[currentIndex]?.docType}
          />
          {images[currentIndex]?.docType && (
            <div className="mt-2 text-center text-white text-sm">
              {images[currentIndex].docType}
            </div>
          )}
        </div>

        <button
          onClick={(e) => { e.stopPropagation(); onNext(); }}
          className="absolute right-4 p-2 text-white hover:bg-white/10 rounded-full transition-colors"
          disabled={images.length <= 1}
          aria-label="Next media"
        >
          <ChevronRightIcon className="h-8 w-8" />
        </button>
      </div>
    </Transition>
  );
};

interface ImageGalleryProps {
  images: Image[];
  className?: string;
  mainImageHeight?: string;
  thumbnailSize?: string;
  showViewer?: boolean;
  showPDFs?: boolean;   // New prop
  pdfOnly?: boolean;    // New prop
}

const ImageGallery: React.FC<ImageGalleryProps> = ({
  images = [],
  className = '',
  mainImageHeight = 'h-64 md:h-[350px]',
  thumbnailSize = 'w-16 h-16',
  showViewer = true,
  showPDFs = false,
  pdfOnly = false,
}) => {
  const filteredImages = useMemo(() => {
    return images.filter(image => {
      const url = image.docUrl;
      if (pdfOnly) return isPDF(url);
      if (!showPDFs && isPDF(url)) return false;
      return true;
    });
  }, [images, showPDFs, pdfOnly]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const goToPrev = useCallback(() => {
    setCurrentIndex(prev => (prev === 0 ? filteredImages.length - 1 : prev - 1));
  }, [filteredImages.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex(prev => (prev === filteredImages.length - 1 ? 0 : prev + 1));
  }, [filteredImages.length]);

  useEffect(() => {
    setCurrentIndex(0);
  }, [filteredImages]);

  if (!filteredImages.length) return null;

  return (
    <div className={className}>
      <div className="relative">
        {/* Main Media */}
        <div
          className={`relative ${mainImageHeight} overflow-hidden rounded-lg mb-4 cursor-pointer`}
          onClick={() => showViewer && setIsViewerOpen(true)}
        >
          <MediaDisplay
            url={filteredImages[currentIndex]?.docUrl}
            type={filteredImages[currentIndex]?.docType}
          />
          {filteredImages[currentIndex]?.docType && (
            <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white p-2">
              <p className="text-sm">{filteredImages[currentIndex].docType}</p>
            </div>
          )}
        </div>

        {/* Navigation Arrows */}
        {filteredImages.length > 1 && (
          <>
            <button
              className="absolute top-1/2 left-2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/75 focus:outline-none"
              onClick={(e) => {
                e.stopPropagation();
                goToPrev();
              }}
              aria-label="Previous media"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>

            <button
              className="absolute top-1/2 right-2 transform -translate-y-1/2 bg-black/50 text-white p-2 rounded-full hover:bg-black/75 focus:outline-none"
              onClick={(e) => {
                e.stopPropagation();
                goToNext();
              }}
              aria-label="Next media"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </>
        )}

        {/* Thumbnails */}
        {filteredImages.length > 1 && (
          <div className="flex space-x-2 mt-2 overflow-x-auto pb-2">
            {filteredImages.map((image, index) => (
              <div
                key={`${image.id}-${index}`}
                className={`flex-shrink-0 cursor-pointer rounded-md overflow-hidden ${index === currentIndex ? 'ring-2 ring-blue-500' : ''
                  } ${thumbnailSize}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentIndex(index);
                }}
              >
                <Thumbnail
                  url={image.docUrl}
                  type={image.docType}
                  active={index === currentIndex}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {showViewer && (
        <ImageViewer
          images={filteredImages}
          isOpen={isViewerOpen}
          currentIndex={currentIndex}
          onClose={() => setIsViewerOpen(false)}
          onNext={goToNext}
          onPrev={goToPrev}
        />
      )}
    </div>
  );
};

export default ImageGallery;
